import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, 
  Target, 
  Users, 
  Globe, 
  Rocket,
  CheckCircle2,
  Building2,
  Lightbulb,
  Heart
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";

export default function AboutPage() {
  const values = [
    {
      icon: Lightbulb,
      title: "Expertise Matters",
      description: "We believe the right insight at the right time can transform businesses. We connect you with people who've been there."
    },
    {
      icon: Globe,
      title: "Global Reach, Local Knowledge",
      description: "Our network spans continents, but our experts bring deep local understanding of markets, cultures, and opportunities."
    },
    {
      icon: Heart,
      title: "Trust & Transparency",
      description: "We build relationships on trust. Clear pricing, verified experts, and honest advice are our foundations."
    },
    {
      icon: Rocket,
      title: "Action-Oriented",
      description: "We don't just provide advice—we help you execute. From insights to hiring, we're partners in your growth."
    }
  ];

  const stats = [
    { value: "1,000+", label: "Sessions Completed" },
    { value: "500+", label: "Verified Experts" },
    { value: "50+", label: "Industries Covered" },
    { value: "7", label: "Focus Markets" },
  ];

  const markets = [
    { name: "UAE", flag: "🇦🇪" },
    { name: "Saudi Arabia", flag: "🇸🇦" },
    { name: "Egypt", flag: "🇪🇬" },
    { name: "India", flag: "🇮🇳" },
    { name: "Singapore", flag: "🇸🇬" },
    { name: "United Kingdom", flag: "🇬🇧" },
    { name: "United States", flag: "🇺🇸" },
  ];

  return (
    <div className="min-h-screen bg-obsidian">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-gold-500/5 via-transparent to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-500 mb-4">ABOUT US</p>
            <h1 className="font-display text-5xl md:text-6xl mb-6">
              Connecting Ambition<br />with Expertise
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed">
              ExpertBase is where businesses meet the experts who can help them grow. 
              We're building the world's most actionable expert network—one that goes 
              beyond advice to help you execute.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 bg-charcoal/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-display text-4xl mb-6">Our Mission</h2>
              <p className="text-xl text-slate-400 leading-relaxed mb-6">
                We believe every business deserves access to world-class expertise. 
                Whether you're a startup entering a new market, a PE firm doing due diligence, 
                or an enterprise building a global team—the right expert can change everything.
              </p>
              <p className="text-slate-400 leading-relaxed">
                ExpertBase was founded in Dubai with a vision to democratize access to expert 
                knowledge. We're not just building a marketplace—we're creating a partner 
                ecosystem that helps businesses understand, enter, and succeed in new markets.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-6"
            >
              {stats.map((stat, index) => (
                <div 
                  key={index}
                  className="bg-charcoal border border-slate-800 p-8 rounded-sm text-center"
                >
                  <div className="font-display text-4xl text-gold-500 mb-2">{stat.value}</div>
                  <div className="text-slate-500 text-sm">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-4xl mb-4">Our Values</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              The principles that guide how we build our platform and serve our community.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                className="p-8 bg-charcoal border border-slate-800 rounded-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <value.icon className="w-10 h-10 text-gold-500 mb-6" />
                <h3 className="font-display text-xl mb-3">{value.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-24 bg-charcoal/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-4xl mb-4">What We Do</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Two powerful services to accelerate your growth.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <motion.div
              className="bg-charcoal border border-slate-800 p-10 rounded-sm"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Users className="w-12 h-12 text-blue-400 mb-6" />
              <h3 className="font-display text-2xl mb-4">Expert Consultations</h3>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Book 1-on-1 sessions with verified experts across industries. 
                Get strategic advice, market insights, and learn from those 
                who've navigated the challenges you're facing.
              </p>
              <ul className="space-y-3">
                {[
                  "500+ verified experts",
                  "50+ industries covered",
                  "Flexible scheduling",
                  "Secure payments"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              className="bg-charcoal border border-slate-800 p-10 rounded-sm"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Building2 className="w-12 h-12 text-gold-500 mb-6" />
              <h3 className="font-display text-2xl mb-4">GTM Team Building</h3>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Expanding into a new market? We help you build the local team 
                to execute your go-to-market strategy. From executives to 
                operations, we find the right people.
              </p>
              <ul className="space-y-3">
                {[
                  "Executive search",
                  "Commercial team hiring",
                  "Local operations setup",
                  "End-to-end support"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-gold-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Focus Markets */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-4xl mb-4">Our Focus Markets</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Deep expertise and on-ground presence in key global markets.
            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-4">
            {markets.map((market, index) => (
              <motion.div
                key={index}
                className="flex items-center gap-3 px-6 py-4 bg-charcoal border border-slate-800 rounded-sm"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
                viewport={{ once: true }}
              >
                <span className="text-2xl">{market.flag}</span>
                <span className="font-medium">{market.name}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-charcoal/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-4xl mb-6">Ready to Get Started?</h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-8">
              Join thousands of businesses and experts on ExpertBase.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/experts">
                <Button className="bg-gold-500 hover:bg-gold-400 text-black font-semibold px-8 py-6 rounded-sm">
                  Browse Experts
                </Button>
              </Link>
              <Link to="/become-expert">
                <Button variant="outline" className="border-slate-700 hover:bg-slate-800 px-8 py-6 rounded-sm">
                  Become an Expert
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
